"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UtxoAccount = exports.AbstractChainAccount = exports.AccountType = void 0;
const transaction_1 = require("../../transaction");
const utils_1 = require("../../utils");
const balance_1 = require("./balance");
var AccountType;
(function (AccountType) {
    AccountType[AccountType["Nonce"] = 0] = "Nonce";
    AccountType[AccountType["Utxo"] = 1] = "Utxo";
})(AccountType || (exports.AccountType = AccountType = {}));
class AbstractChainAccount {
    type;
    chain;
    balances = new Map();
    chainWallet;
    address;
    signers;
    constructor(type, chain, wallet) {
        this.type = type;
        this.chain = chain;
        this.chainWallet = wallet.getWallet(chain);
        this.address = this.chainWallet.getAddress();
        this.signers = [this.chainWallet];
    }
    getAssetValue(asset) {
        return asset.getAssetValue(this.getAmount(asset.assetId));
    }
    async getValue(provider, assetId) {
        const asset = await this.chain.getAsset(provider, assetId);
        return this.getAssetValue(asset);
    }
    getAmount(assetId) {
        return this.getBalance(assetId).getValue();
    }
    getBalance(assetId) {
        if (!this.balances.has(assetId)) {
            this.balances.set(assetId, new balance_1.Balance());
        }
        return this.balances.get(assetId);
    }
    getSignersAddresses() {
        const addresses = [];
        for (const signer of this.signers) {
            addresses.push(signer.getAddress());
        }
        return addresses;
    }
    async fetchBalances(assets) {
        const fetchers = [];
        for (const asset of assets) {
            const assetId = typeof asset === 'string' ? asset : asset.assetId;
            fetchers.push(this.fetchBalance(assetId));
        }
        await Promise.all(fetchers);
    }
    spend(spendings) {
        for (const spending of spendings) {
            this.getBalance(spending.assetId).spend(spending.amount);
        }
    }
}
exports.AbstractChainAccount = AbstractChainAccount;
class UtxoAccount extends AbstractChainAccount {
    utxoSet = [];
    utxoSetMultiSig = [];
    utxoSetLocked = [];
    utxoSetStakeable = [];
    lockedBalances = new Map();
    stakeableBalances = new Map();
    fetching = false;
    utxoApi;
    constructor(chain, utxoApi, wallet) {
        super(AccountType.Utxo, chain, wallet);
        this.utxoApi = utxoApi;
    }
    getLockedAssetValue(asset) {
        return asset.getAssetValue(this.getLockedAmount(asset.assetId));
    }
    async getLockedValue(provider, assetId) {
        const asset = await this.chain.getAsset(provider, assetId);
        return this.getLockedAssetValue(asset);
    }
    getLockedAmount(assetId) {
        return this.getLockedBalance(assetId).getValue();
    }
    getLockedBalance(assetId) {
        if (!this.lockedBalances.has(assetId)) {
            this.lockedBalances.set(assetId, new balance_1.Balance());
        }
        return this.lockedBalances.get(assetId);
    }
    getStakeableAssetValue(asset) {
        return asset.getAssetValue(this.getStakeableAmount(asset.assetId));
    }
    async getStakeableValue(provider, assetId) {
        const asset = await this.chain.getAsset(provider, assetId);
        return this.getStakeableAssetValue(asset);
    }
    getStakeableAmount(assetId) {
        return this.getStakeableBalance(assetId).getValue();
    }
    getStakeableBalance(assetId) {
        if (!this.stakeableBalances.has(assetId)) {
            this.stakeableBalances.set(assetId, new balance_1.Balance());
        }
        return this.stakeableBalances.get(assetId);
    }
    async fetchBalance(assetId) {
        // there is currently no other way to do it only with utxos
        // a seperated indexing of each asset is needed to be able to do it
        await this.refreshBalances();
    }
    async fetchBalances(assets) {
        // assets here are not useful because of utxos
        if (this.fetching) {
            return;
        }
        this.fetching = true;
        this.utxoSet = await (0, utils_1.fetchUtxos)(this.utxoApi, [this.address]);
        this.sortUtxoSet();
        (0, utils_1.calculateBalances)(this.utxoSet, this.balances);
        (0, utils_1.calculateBalances)(this.utxoSetLocked, this.lockedBalances);
        (0, utils_1.calculateBalances)(this.utxoSetStakeable, this.stakeableBalances);
        this.fetching = false;
    }
    async refreshBalances() {
        await this.fetchBalances([]);
    }
    spend(spendings) {
        super.spend(spendings);
        const utxos = [];
        for (const utxo of this.utxoSet) {
            let spent = false;
            for (const spending of spendings) {
                for (const spend of spending.utxos) {
                    const sameTransaction = spend.transactionId.value === utxo.transactionId.value;
                    const sameIndex = spend.utxoIndex === utxo.utxoIndex;
                    if (sameTransaction && sameIndex) {
                        spent = true;
                        break;
                    }
                }
                if (spent) {
                    break;
                }
            }
            if (!spent) {
                utxos.push(utxo);
            }
        }
        this.utxoSet = utxos;
    }
    sortUtxoSet() {
        const spendableUtxoSet = [];
        this.utxoSetMultiSig = [];
        this.utxoSetLocked = [];
        this.utxoSetStakeable = [];
        const currentTime = utils_1.TimeUtils.now();
        for (const utxo of this.utxoSet) {
            if (utxo.output.locktime > currentTime) {
                this.utxoSetLocked.push(utxo);
                if (utxo.output.typeId === transaction_1.StakeableLockedOutputTypeId) {
                    this.utxoSetStakeable.push(utxo);
                }
            }
            else if (!this.hasThreshold(utxo)) {
                this.utxoSetMultiSig.push(utxo);
            }
            else {
                spendableUtxoSet.push(utxo);
                this.utxoSetStakeable.push(utxo);
            }
        }
        this.utxoSet = spendableUtxoSet;
    }
    hasThreshold(utxo) {
        let value = 0;
        // There could be duplicate addresses in signers list. User could input a mnemonic and then a private key in the vault.
        // Make sure every address is only used once when verifying the threshold.
        const usedAddresses = new Set();
        for (const address of utxo.output.addresses) {
            for (const signer of this.signers) {
                if (!usedAddresses.has(signer.getAddress()) && address.matches(signer.getAddress())) {
                    value += 1;
                    if (value >= utxo.output.threshold) {
                        return true;
                    }
                    usedAddresses.add(signer.getAddress());
                    continue;
                }
            }
        }
        return false;
    }
}
exports.UtxoAccount = UtxoAccount;
